import React, { useEffect, useMemo, useState } from 'react';
import api, { companiesAPI, employeesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  CalendarDays, Download, Loader2, RefreshCcw, FileSpreadsheet,
  FileText, FileType2, Filter, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const PRESETS = [
  { v: 'today', l: 'Today' },
  { v: 'yesterday', l: 'Yesterday' },
  { v: 'this_week', l: 'This Week' },
  { v: 'last_week', l: 'Last Week' },
  { v: 'this_month', l: 'This Month' },
  { v: 'last_month', l: 'Last Month' },
  { v: 'custom', l: 'Custom Range' },
];

const VIEW_MODES = [
  { v: 'linear', l: 'Linear' },
  { v: 'weekly', l: 'Weekly Grid' },
  { v: 'monthly', l: 'Monthly Grid' },
];

const PREVIEW_LIMIT = 200;

const CODE_STYLE = {
  P:  'bg-emerald-100 text-emerald-700',
  A:  'bg-red-100 text-red-700',
  L:  'bg-amber-100 text-amber-700',
  W:  'bg-sky-100 text-sky-700',
  OD: 'bg-indigo-100 text-indigo-700',
  H:  'bg-fuchsia-100 text-fuchsia-700',
  WO: 'bg-zinc-100 text-zinc-600',
};

const GridCell = ({ cell }) => {
  if (!cell) return <span className="text-slate-300">·</span>;
  const cls = CODE_STYLE[cell.code] || 'bg-slate-100 text-slate-700';
  const tip = `${cell.status}${cell.in ? ` | In ${cell.in}` : ''}${cell.out ? ` | Out ${cell.out}` : ''}${cell.late ? ` | ${cell.late}` : ''}${cell.early ? ` | ${cell.early}` : ''}`;
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[22px] px-1 py-0.5 rounded text-[10px] font-semibold ${cls}`}
      title={tip}
    >
      {cell.code || '-'}
    </span>
  );
};

const statusBadge = (status) => {
  const s = (status || '').toLowerCase();
  let cls = 'bg-slate-100 text-slate-700 border-slate-200';
  if (s.startsWith('present')) cls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
  else if (s.startsWith('absent')) cls = 'bg-red-100 text-red-700 border-red-200';
  else if (s.startsWith('leave')) cls = 'bg-amber-100 text-amber-700 border-amber-200';
  else if (s.startsWith('wfh')) cls = 'bg-sky-100 text-sky-700 border-sky-200';
  else if (s.startsWith('od')) cls = 'bg-indigo-100 text-indigo-700 border-indigo-200';
  else if (s.startsWith('holiday')) cls = 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200';
  else if (s.startsWith('weekoff')) cls = 'bg-zinc-100 text-zinc-700 border-zinc-200';
  return <Badge variant="outline" className={cls}>{status}</Badge>;
};

const AttendanceReport = () => {
  const { user } = useAuth() || {};
  const isManager = user?.role === 'manager';

  const [preset, setPreset] = useState('this_month');
  const [viewMode, setViewMode] = useState('linear');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const now = new Date();
  const [gridMonth, setGridMonth] = useState(now.getMonth() + 1);
  const [gridYear, setGridYear] = useState(now.getFullYear());
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companyId, setCompanyId] = useState('all');
  const [department, setDepartment] = useState('all');
  const [employeeId, setEmployeeId] = useState('all');
  const [workStart, setWorkStart] = useState('09:30');
  const [workEnd, setWorkEnd] = useState('17:30');
  const [grace, setGrace] = useState(15);
  const [shiftLocked, setShiftLocked] = useState(true); // use org Attendance Settings by default

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState(null);
  const [total, setTotal] = useState(0);
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState('');

  // Load lookups
  useEffect(() => {
    (async () => {
      try {
        const [cRes, eRes] = await Promise.all([
          companiesAPI.getAll().catch(() => ({ data: [] })),
          employeesAPI.getAll({ limit: 2000 }).catch(() => ({ data: [] })),
        ]);
        setCompanies(cRes.data || []);
        setEmployees(eRes.data || []);
      } catch {
        // ignore
      }
    })();
  }, []);

  const departments = useMemo(() => {
    const s = new Set();
    (employees || []).forEach((e) => e.department && s.add(e.department));
    return Array.from(s).sort();
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    let list = employees;
    if (companyId !== 'all') list = list.filter((e) => e.company_id === companyId);
    if (department !== 'all') list = list.filter((e) => e.department === department);
    return list;
  }, [employees, companyId, department]);

  const buildParams = (fmt) => {
    const p = {};
    // Only pass shift params when user explicitly unlocks (otherwise fall back
    // to the org-wide Attendance Settings on the backend).
    if (!shiftLocked) {
      p.work_start = workStart;
      p.work_end = workEnd;
      p.grace_minutes = grace;
    }
    if (viewMode === 'monthly') {
      p.month = gridMonth;
      p.year = gridYear;
    } else {
      p.preset = preset;
      if (preset === 'custom') {
        if (!startDate || !endDate) {
          toast.error('Pick a start and end date for Custom range');
          return null;
        }
        p.start_date = startDate;
        p.end_date = endDate;
      }
    }
    if (companyId !== 'all') p.company_id = companyId;
    if (department !== 'all') p.department = department;
    if (employeeId !== 'all') p.employee_id = employeeId;
    if (fmt) p.format = fmt;
    return p;
  };

  const runReport = async () => {
    const params = buildParams();
    if (!params) return;
    setLoading(true);
    try {
      if (viewMode === 'linear') {
        const res = await api.get('/reports/attendance-report', {
          params: { ...params, limit: PREVIEW_LIMIT },
        });
        setRows(res.data?.rows || []);
        setSummary(res.data?.summary || null);
        setRange(res.data?.range || null);
        setTotal(res.data?.total || 0);
        setGrid(null);
      } else {
        const url = viewMode === 'monthly'
          ? '/reports/attendance-report/monthly-grid'
          : '/reports/attendance-report/weekly-grid';
        const res = await api.get(url, { params });
        setGrid(res.data);
        setSummary(res.data?.summary || null);
        setRange(res.data?.range || null);
        setTotal((res.data?.employees || []).length * (res.data?.days || []).length);
        setRows([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setRows([]); setSummary(null); setRange(null); setTotal(0); setGrid(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); /* initial + on view change */ // eslint-disable-next-line
  }, [viewMode]);

  const doExport = async (fmt) => {
    const params = buildParams(fmt);
    if (!params) return;
    setDownloading(fmt);
    try {
      const res = await api.get('/reports/attendance-report/export', {
        params, responseType: 'blob',
      });
      const stamp = `${params.start_date || range?.start || 'range'}_${params.end_date || range?.end || ''}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance_report_${stamp}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setDownloading('');
    }
  };

  const summaryTiles = [
    { k: 'total_rows', l: 'Rows', color: 'text-slate-900' },
    { k: 'present', l: 'Present', color: 'text-emerald-600' },
    { k: 'absent', l: 'Absent', color: 'text-red-600' },
    { k: 'leave', l: 'Leave', color: 'text-amber-600' },
    { k: 'wfh', l: 'WFH', color: 'text-sky-600' },
    { k: 'od', l: 'OD', color: 'text-indigo-600' },
    { k: 'holiday', l: 'Holiday', color: 'text-fuchsia-600' },
    { k: 'weekoff', l: 'Weekoff', color: 'text-zinc-600' },
    { k: 'late', l: 'Late', color: 'text-orange-600' },
    { k: 'early_out', l: 'Early Out', color: 'text-orange-600' },
    { k: 'total_hours', l: 'Total Hrs', color: 'text-slate-900' },
  ];

  return (
    <div className="space-y-6" data-testid="attendance-report-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Attendance Report
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {isManager
              ? 'View your team\'s attendance across daily, weekly, monthly, or a custom range.'
              : 'Enterprise attendance analytics with Excel, CSV and PDF export.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={runReport}
            disabled={loading}
            data-testid="attendance-report-refresh"
          >
            {loading
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <RefreshCcw className="w-4 h-4 mr-2" />}
            Run Report
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={!!downloading || loading} data-testid="attendance-report-export-btn">
                {downloading
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Download className="w-4 h-4 mr-2" />}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-50">
              <DropdownMenuItem
                onClick={() => doExport('xlsx')}
                data-testid="attendance-report-export-xlsx"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => doExport('csv')}
                data-testid="attendance-report-export-csv"
              >
                <FileText className="w-4 h-4 mr-2" /> CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => doExport('pdf')}
                data-testid="attendance-report-export-pdf"
              >
                <FileType2 className="w-4 h-4 mr-2" /> PDF (.pdf)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="attendance-report-filters">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-slate-600">View</Label>
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger data-testid="attendance-report-view">
                  <SelectValue placeholder="View" />
                </SelectTrigger>
                <SelectContent>
                  {VIEW_MODES.map((v) => (
                    <SelectItem key={v.v} value={v.v}>{v.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {viewMode !== 'monthly' && (
              <div>
                <Label className="text-xs text-slate-600">Range</Label>
                <Select value={preset} onValueChange={setPreset}>
                  <SelectTrigger data-testid="attendance-report-preset">
                    <SelectValue placeholder="Range" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESETS.map((p) => (
                      <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {viewMode === 'monthly' && (
              <>
                <div>
                  <Label className="text-xs text-slate-600">Month</Label>
                  <Select value={String(gridMonth)} onValueChange={(v) => setGridMonth(Number(v))}>
                    <SelectTrigger data-testid="attendance-report-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {new Date(2026, m - 1, 1).toLocaleString('en', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Year</Label>
                  <Input type="number" value={gridYear}
                         onChange={(e) => setGridYear(parseInt(e.target.value || '0', 10))}
                         data-testid="attendance-report-year" />
                </div>
              </>
            )}

            {viewMode !== 'monthly' && preset === 'custom' && (
              <>
                <div>
                  <Label className="text-xs text-slate-600">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="attendance-report-start-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="attendance-report-end-date"
                  />
                </div>
              </>
            )}

            {!isManager && (
              <div>
                <Label className="text-xs text-slate-600">Company</Label>
                <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setEmployeeId('all'); }}>
                  <SelectTrigger data-testid="attendance-report-company">
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-600">Department</Label>
              <Select value={department} onValueChange={(v) => { setDepartment(v); setEmployeeId('all'); }}>
                <SelectTrigger data-testid="attendance-report-department">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Employee</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger data-testid="attendance-report-employee">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Employees</SelectItem>
                  {filteredEmployees.slice(0, 500).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.employee_code ? `${e.employee_code} — ` : ''}
                      {e.first_name} {e.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Shift Source</Label>
              <Select value={shiftLocked ? 'org' : 'override'} onValueChange={(v) => setShiftLocked(v === 'org')}>
                <SelectTrigger data-testid="attendance-report-shift-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="org">Use Attendance Settings</SelectItem>
                  <SelectItem value="override">Override for this report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!shiftLocked && (
              <>
                <div>
                  <Label className="text-xs text-slate-600">Shift Start</Label>
                  <Input
                    type="time"
                    value={workStart}
                    onChange={(e) => setWorkStart(e.target.value)}
                    data-testid="attendance-report-work-start"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Shift End</Label>
                  <Input
                    type="time"
                    value={workEnd}
                    onChange={(e) => setWorkEnd(e.target.value)}
                    data-testid="attendance-report-work-end"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Grace (min)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="120"
                    value={grace}
                    onChange={(e) => setGrace(parseInt(e.target.value || '0', 10))}
                    data-testid="attendance-report-grace"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {summary && (
        <div
          className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-3"
          data-testid="attendance-report-summary"
        >
          {summaryTiles.map((t) => (
            <Card key={t.k}>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500">{t.l}</div>
                <div className={`text-2xl font-semibold ${t.color}`}>
                  {summary[t.k] ?? 0}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Table (Linear view) */}
      {viewMode === 'linear' && (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="w-4 h-4" />
            {range
              ? `Preview ${range.start} → ${range.end}`
              : 'Preview'}
          </CardTitle>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" />
            Showing <b>{rows.length}</b> of <b>{total}</b> rows
            {total > rows.length && ' — export for the full dataset'}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="py-10 text-center text-slate-400">
              <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-400" data-testid="attendance-report-empty">
              No data for the selected filters.
            </div>
          ) : (
            <Table data-testid="attendance-report-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Dept</TableHead>
                  <TableHead>In</TableHead>
                  <TableHead>Out</TableHead>
                  <TableHead>Hrs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Late</TableHead>
                  <TableHead>Early</TableHead>
                  <TableHead className="hidden lg:table-cell">In Loc.</TableHead>
                  <TableHead className="hidden lg:table-cell">Device</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="whitespace-nowrap">
                      <div>{r.Date}</div>
                      <div className="text-xs text-slate-400">{r.Day}</div>
                    </TableCell>
                    <TableCell>{r['Employee Code']}</TableCell>
                    <TableCell className="whitespace-nowrap">{r['Employee Name']}</TableCell>
                    <TableCell>{r.Department}</TableCell>
                    <TableCell>{r['Punch In']}</TableCell>
                    <TableCell>{r['Punch Out']}</TableCell>
                    <TableCell>{r['Total Hours']}</TableCell>
                    <TableCell>{statusBadge(r.Status)}</TableCell>
                    <TableCell className="text-orange-600">{r.Late}</TableCell>
                    <TableCell className="text-orange-600">{r['Early Out']}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-slate-500">
                      {r['Punch In Location']}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-slate-500">
                      {r.Device}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      )}

      {/* Weekly / Monthly Grid view */}
      {viewMode !== 'linear' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="w-4 h-4" />
              {viewMode === 'monthly' ? 'Monthly Attendance Grid' : 'Weekly Attendance Grid'}
              {range && (
                <span className="text-xs text-slate-500 font-normal ml-2">
                  {range.start} → {range.end}
                </span>
              )}
            </CardTitle>
            <div className="text-xs text-slate-500 flex items-center gap-3">
              {Object.entries(CODE_STYLE).map(([code, cls]) => (
                <span key={code} className="flex items-center gap-1">
                  <span className={`${cls} px-1 rounded font-semibold text-[10px]`}>{code}</span>
                </span>
              ))}
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="py-10 text-center text-slate-400">
                <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading…
              </div>
            ) : !grid || !(grid.employees || []).length ? (
              <div className="py-10 text-center text-slate-400" data-testid="attendance-grid-empty">
                No data for the selected filters.
              </div>
            ) : (
              <table className="w-full border-collapse" data-testid="attendance-grid">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b">
                    <th className="sticky left-0 bg-white z-10 py-2 pr-2 min-w-[160px]">Employee</th>
                    <th className="py-2 pr-2 min-w-[90px]">Dept</th>
                    {grid.days.map((d) => (
                      <th key={d.date}
                          className={`py-2 px-1 text-center min-w-[32px] ${d.is_weekend ? 'text-fuchsia-500' : ''}`}
                          title={d.date}>
                        <div className="text-[10px] font-normal">{d.day}</div>
                        <div className="text-xs font-semibold">
                          {viewMode === 'monthly' ? d.day_num : d.date.slice(5)}
                        </div>
                      </th>
                    ))}
                    <th className="py-2 px-2 text-center min-w-[36px]">P</th>
                    <th className="py-2 px-2 text-center min-w-[36px]">A</th>
                    <th className="py-2 px-2 text-center min-w-[36px]">L</th>
                    <th className="py-2 px-2 text-center min-w-[36px]">OD</th>
                    <th className="py-2 px-2 text-center min-w-[48px]">Hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {grid.employees.map((emp) => (
                    <tr key={emp.key} className="border-b hover:bg-slate-50">
                      <td className="sticky left-0 bg-white z-10 py-2 pr-2 text-sm whitespace-nowrap">
                        <div className="font-medium">{emp.name}</div>
                        <div className="text-[10px] text-slate-400">{emp.employee_code}</div>
                      </td>
                      <td className="py-2 pr-2 text-xs text-slate-500">{emp.department}</td>
                      {grid.days.map((d) => (
                        <td key={d.date} className="py-1 px-0.5 text-center">
                          <GridCell cell={grid.matrix?.[emp.key]?.[d.date]} />
                        </td>
                      ))}
                      <td className="py-2 px-2 text-center text-xs text-emerald-600 font-semibold">
                        {grid.totals?.[emp.key]?.present ?? 0}
                      </td>
                      <td className="py-2 px-2 text-center text-xs text-red-600 font-semibold">
                        {grid.totals?.[emp.key]?.absent ?? 0}
                      </td>
                      <td className="py-2 px-2 text-center text-xs text-amber-600 font-semibold">
                        {grid.totals?.[emp.key]?.leave ?? 0}
                      </td>
                      <td className="py-2 px-2 text-center text-xs text-indigo-600 font-semibold">
                        {grid.totals?.[emp.key]?.od ?? 0}
                      </td>
                      <td className="py-2 px-2 text-center text-xs font-semibold">
                        {grid.totals?.[emp.key]?.total_hours ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AttendanceReport;
